/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Asks the server how many places each activity of one event still has.
 *
 * WHY THIS EXISTS. `max_participants` is the size of the room, not the number
 * of empty chairs. The entry modal only ever knew the first, so it offered
 * seven seats on an activity with three left, wrote five, and reported the rest
 * as refused — a correct outcome delivered in the worst possible order. This is
 * the number that lets the stepper stop before the refusal happens.
 *
 * ONE CALL FOR THE WHOLE EVENT, not one per activity. The rows are fetched by
 * `event_id` and counted per `event_activity_id` here, so an event with six
 * activities still costs one request. That matters because this runs on a press
 * of Continue, with the visitor watching.
 *
 * THE SERVER COUNTS NOW, NOT THIS FILE. It used to read the registration rows
 * themselves -- `fields: ['event_activity_id']`, up to five thousand of them --
 * and tally them here. That only ever worked for an organization admin: the
 * batch read applies no row-level security, so a plain read right on that table
 * grants the WHOLE table, and no ordinary participant can be given one. Every
 * normal user -- which is to say every real customer -- got 403 and no check at
 * all.
 *
 * A saved query answers the same question without that problem, because it
 * returns four integers per activity instead of one row per registration.
 * Nothing about anybody crosses the wire, so there is nothing to grant. See
 * seed_analytics_queries.AVAILABILITY_SQL.
 *
 * IT STILL COUNTS ROWS, BECAUSE SEATS ARE ROWS. Three seats is three
 * registrations, so the count of rows against an activity IS the number of
 * places taken. The same fact EventEntryModal's header comment records --
 * the counting simply happens in Postgres now.
 *
 * IT FAILS TO "DO NOT KNOW", NEVER TO "FULL". Every failure — no read
 * permission, a dropped connection, a reply that will not parse, a page of rows
 * that might be truncated — returns null, and the caller carries on exactly as
 * it did before this file existed. That direction is not arbitrary: a wrong
 * "unknown" costs one refusal the visitor would have had anyway, while a wrong
 * "full" turns a visitor away from an event that had room for them.
 *
 * IT IS NOT ENFORCEMENT. Two people can pass this check a millisecond apart and
 * both be told there is one seat left. The capacity rule inside the write is
 * still the only thing that cannot be raced, and the per-seat report the modal
 * already draws is still how a seat lost in that millisecond is explained.
 */

import APIHelper from '../../../api/APIHelper';
import envConfig from '../../../config/envConfig';
// Needed while this file read the registration table itself. The saved query
// names the table and the statuses now, so nothing here does.
// import { REGISTRATION_TABLE, ACTIVE_STATUSES } from './fetchMyRegistrations';

const api = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/* Named the batch-read block in the reply. The saved query is addressed by its
   own name in the URL, so there is no block to find. */
// const RESULT_KEY = 'seats';

/**
 * The row ceiling the old table read asked for, kept for one reason: it records
 * a failure mode that no longer exists.
 *
 * Reading the rows themselves meant a reply that filled this limit might have
 * been CUT SHORT, and a truncated list is a floor on what is taken rather than
 * a count of it. Reporting that floor would have overstated what was left on
 * precisely the events where the number matters most — the big ones, nearly
 * full — so a full page was treated as no answer at all.
 *
 * The saved query returns one row per ACTIVITY, so an event with fifty
 * thousand entries still answers in a handful of rows and nothing can be
 * truncated. Kept exported because it is still referenced by tests that pin
 * this history; nothing in the fetch path reads it any more.
 */
export const AVAILABILITY_ROW_LIMIT = 5000;

/**
 * The saved query this asks for, by name, and the columns it reads back.
 *
 * The reply is positional -- `rows` are arrays and `columns` names each slot --
 * so the indexes are resolved from `columns` rather than assumed. A query
 * edited to return its columns in another order then still reads correctly,
 * and one missing a column it needs is treated as no answer rather than
 * silently counting the wrong field.
 */
export const AVAILABILITY_QUERY = 'event_activity_availability';
const ACTIVITY_COLUMN = 'event_activity_id';
const TAKEN_COLUMN = 'confirmed_registrations';

/**
 * The refusals APIHelper reports as a THROW rather than as a response.
 *
 * A 401/403 never reaches `resp.ok` here: APIHelper reads the body and throws a
 * typed Error. Three of those types are DECISIONS — nobody is signed in, the
 * session went, the token is not valid for this — and asking again only
 * collects the same answer at the cost of a second round trip on a press the
 * visitor is watching. Anything else (a dropped connection, a reset) is a
 * fault, and a fault is exactly what the one retry exists for.
 */
const SETTLED_REFUSALS = new Set(['session_expired', 'requires_auth', 'oauth_token_invalid']);

/**
 * How many places an activity has left, or null when that cannot be known.
 *
 * Null rather than Infinity for an activity with no cap: "uncapped" and "we do
 * not know the cap" are the same thing to a stepper, and both must leave the
 * existing burst guard in charge rather than replacing it with a bigger number.
 *
 * Never negative. An activity that oversold — a race the database lost, rows
 * written before a cap was lowered — has nothing left, and "-2 places" is not a
 * sentence any part of the UI should have to render.
 */
export function remainingSeats(activity, takenCount) {
  const capacity = Number(activity?.max_participants);
  if (!Number.isFinite(capacity) || capacity <= 0) return null;
  const taken = Number.isFinite(takenCount) ? takenCount : 0;
  return Math.max(0, capacity - taken);
}

/**
 * Places left per activity id, as a Map, or null when the question could not
 * be answered.
 *
 * Activities whose capacity cannot be read are absent from the map rather than
 * present with a null — `has()` then answers "do we know about this one?"
 * without a caller having to tell a missing key from a null value.
 *
 * ONE RETRY, and only for a genuine fault, exactly as fetchMyRegistrations
 * does. A refusal is a settled answer and asking again only repeats it; a
 * thrown call is not settled, and the second attempt is where an expired token
 * or a dropped connection comes good.
 */
export async function fetchActivityAvailability({ eventId, activities, retries = 1 }) {
  if (eventId === undefined || eventId === null || eventId === '') return null;
  const items = Array.isArray(activities) ? activities.filter(a => a && a.id != null) : [];
  if (items.length === 0) return null;

  try {
    // GET, by name. The SQL lives on the server; only the event id travels, as
    // a declared parameter the engine binds rather than interpolates.
    const resp = await api.apiCall(
      `/arcee/application/${envConfig.applicationId}/analytics/${AVAILABILITY_QUERY}/run/`
      + `?event_id=${encodeURIComponent(eventId)}`,
      {
        method: 'GET',
        credentials: 'include',
        // NEVER RAISE THE SESSION DIALOG FROM HERE. On a session refusal
        // APIHelper dispatches a global `session-expired` event, and
        // SessionExpiryContext answers it with a blocking dialog that sends the
        // visitor to /sign-in. That belongs to the action they actually took —
        // the write — which raises it on its own and says what it was trying to
        // do. This is an optional background count nobody asked for, and a
        // question asked quietly must not be the thing that interrupts them.
        suppressSessionEvent: true,
      },
    );

    // A refusal is an unanswerable question, not an error to report. The query
    // is signed_in, so a visitor with no session gets 401 here and the stepper
    // keeps the ceilings it had before this call existed.
    if (!resp.ok) return null;

    const body = await resp.json().catch(() => null);
    const columns = body && Array.isArray(body.columns) ? body.columns : null;
    const rows = body && Array.isArray(body.rows) ? body.rows : null;
    if (!columns || !rows) return null;

    // Resolved by name; see AVAILABILITY_QUERY for why they are not assumed.
    const at = name => columns.findIndex(c => c && c.name === name);
    const activityAt = at(ACTIVITY_COLUMN);
    const takenAt = at(TAKEN_COLUMN);
    if (activityAt < 0 || takenAt < 0) return null;

    const taken = new Map();
    rows.forEach((row) => {
      if (!Array.isArray(row)) return;
      const id = row[activityAt];
      if (id === undefined || id === null) return;
      const count = Number(row[takenAt]);
      taken.set(id, Number.isFinite(count) ? count : 0);
    });

    const left = new Map();
    items.forEach(activity => {
      const remaining = remainingSeats(activity, taken.get(activity.id) || 0);
      if (remaining !== null) left.set(activity.id, remaining);
    });
    return left;
  } catch (e) {
    // A settled refusal is an answer. Only a fault earns the second attempt.
    if (retries > 0 && !SETTLED_REFUSALS.has(e?.type)) {
      return fetchActivityAvailability({ eventId, activities, retries: retries - 1 });
    }
    return null;
  }
}
